import { useLocation } from "react-router-dom";

import Seo, { SITE_URL } from "./Seo";

const DEFAULT_SEO = {
  title: "Wedding Stationery | Premium Wedding Invitations & Cards Online",
  description:
    "Shop premium wedding invitations, save the date cards, RSVP cards and custom wedding stationery for every celebration.",
};

const ROUTE_SEO = {
  "/": DEFAULT_SEO,
  "/shop": {
    title: "Shop Wedding Invitations & Stationery | Graphe Weddings",
    description:
      "Browse elegant wedding invitations, save the date cards, RSVP cards and personalised wedding stationery online.",
  },
  "/shop-wedding": {
    title: "Wedding Stationery Collection | Graphe Weddings",
    description:
      "Discover coordinated wedding stationery and invitation designs created for elegant, memorable celebrations.",
  },
  "/faqs": {
    title: "Wedding Stationery FAQs | Graphe Weddings",
    description:
      "Find answers about wedding stationery customisation, ordering, payments, delivery and digital products.",
  },
  "/about-us": {
    title: "About Graphe Weddings | Wedding Stationery Studio",
    description:
      "Learn about Graphe Weddings and our approach to elegant, personalised stationery for weddings and celebrations.",
  },
  "/contact-us": {
    title: "Contact Graphe Weddings | Wedding Stationery Support",
    description:
      "Contact Graphe Weddings for help with wedding invitations, personalised stationery, orders and delivery.",
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions | Graphe Weddings",
    description: "Read the terms and conditions for using Graphe Weddings.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Graphe Weddings",
    description: "Learn how Graphe Weddings collects, uses and protects personal information.",
  },
  "/cookie-policy": {
    title: "Cookie Policy | Graphe Weddings",
    description: "Learn how Graphe Weddings uses essential and optional cookies.",
  },
  "/refund-policy": {
    title: "Refund Policy | Graphe Weddings",
    description: "Read the refund and cancellation policy for Graphe Weddings orders.",
  },
  "/shipping-policy": {
    title: "Shipping Policy | Graphe Weddings",
    description: "Read about processing, shipping and delivery for Graphe Weddings orders.",
  },
  "/cart": {
    title: "Your Shopping Cart | Graphe Weddings",
    description: "Review the wedding stationery saved in your Graphe Weddings shopping cart.",
  },
  "/checkout": {
    title: "Secure Checkout | Graphe Weddings",
    description: "Complete your Graphe Weddings order using our secure checkout.",
  },
  "/profile": {
    title: "My Profile | Graphe Weddings",
    description: "Manage your Graphe Weddings customer profile, addresses and preferences.",
  },
  "/payment-return": {
    title: "Confirming Payment | Graphe Weddings",
    description: "Securely confirming the payment status for your Graphe Weddings order.",
  },
  "/orders": {
    title: "My Orders | Graphe Weddings",
    description: "View your Graphe Weddings orders, delivery progress and invoices.",
  },
  "/notifications": {
    title: "My Notifications | Graphe Weddings",
    description: "View payment, order, shipping and account updates from Graphe Weddings.",
  },
  "/wishlist": {
    title: "My Wishlist | Graphe Weddings",
    description: "View wedding stationery saved to your Graphe Weddings wishlist.",
  },
  "/disputes": {
    title: "Order Support | Graphe Weddings",
    description: "View and manage support requests for your Graphe Weddings orders.",
  },
  "/login": {
    title: "Customer Login | Graphe Weddings",
    description: "Sign in to your Graphe Weddings customer account.",
  },
  "/signup": {
    title: "Create an Account | Graphe Weddings",
    description: "Create your Graphe Weddings account to order personalised wedding stationery.",
  },
  "/forgot-password": {
    title: "Forgot Password | Graphe Weddings",
    description: "Request a secure password reset for your Graphe Weddings account.",
  },
  "/reset-password": {
    title: "Reset Password | Graphe Weddings",
    description: "Choose a new password for your Graphe Weddings account.",
  },
};

const DYNAMIC_ROUTE_SEO = [
  {
    prefix: "/shop-details/",
    seo: {
      title: "Wedding Stationery Product | Graphe Weddings",
      description: "View product details, pricing and availability from Graphe Weddings.",
    },
  },
  {
    prefix: "/order-success/",
    seo: {
      title: "Payment Successful | Graphe Weddings",
      description: "Your payment was successful and your Graphe Weddings order is confirmed.",
    },
  },
  {
    prefix: "/order-cancelled/",
    seo: {
      title: "Payment Not Completed | Graphe Weddings",
      description: "Review an incomplete Graphe Weddings payment attempt and safely try again.",
    },
  },
];

const PRIVATE_PATH_PREFIXES = [
  "/cart",
  "/checkout",
  "/profile",
  "/order-success",
  "/order-cancelled",
  "/orders",
  "/notifications",
  "/wishlist",
  "/disputes",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
];

const SITE_STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Graphe Weddings",
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/logo512.png`,
      email: "contact@grapheweddings.com",
      sameAs: [
        "https://www.facebook.com/grapheweddings",
        "https://www.instagram.com/grapheweddings/",
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "Graphe Weddings",
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
  ],
};

const RouteSeo = () => {
  const { pathname } = useLocation();
  const normalizedPath =
    pathname === "/" ? pathname : pathname.replace(/\/+$/, "");
  const isPrivate = PRIVATE_PATH_PREFIXES.some(
    (prefix) =>
      normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  );
  const dynamicRoute = DYNAMIC_ROUTE_SEO.find(({ prefix }) =>
    normalizedPath.startsWith(prefix)
  );
  const seo = ROUTE_SEO[normalizedPath] || dynamicRoute?.seo || {
    title: "Page Not Found | Graphe Weddings",
    description: "The requested page could not be found.",
  };
  const isKnownRoute = Boolean(ROUTE_SEO[normalizedPath] || dynamicRoute);

  return (
    <Seo
      {...seo}
      path={normalizedPath}
      robots={
        isPrivate || !isKnownRoute
          ? "noindex, nofollow"
          : "index, follow, max-image-preview:large"
      }
      structuredData={normalizedPath === "/" ? SITE_STRUCTURED_DATA : null}
      structuredDataId="site"
    />
  );
};

export default RouteSeo;
